import React, { useState, useEffect } from 'react';
import {
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Fab,
  Stack,
  Paper,
  Typography,
  Box,
  TextareaAutosize,
  Button
} from '@mui/material';
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from '@mui/icons-material/Cancel';
import IconButton from '@mui/material/IconButton';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { fetchSelectOptions, validateFile, currentDate } from '../../../utils/formUtils';

const FormField = ({ structure, id, values, setValues, showPaypalFields }) => {
  const [selectOptions, setSelectOptions] = useState([]);
  const [filePreview, setFilePreview] = useState(null);
  const [multipleTexts, setMultipleTexts] = useState([]);

  useEffect(() => {
    if (['select', 'select2', 'select9'].includes(structure.inputType)) {
      fetchSelectOptions("getcategory", structure.data || structure.data4 || structure.data9)
        .then(options => setSelectOptions(options));
    }
  }, [structure.inputType, structure.data, structure.data4, structure.data9]);

  const handleChange = (event) => {
    const { name, value, checked, type, files } = event.target;
    if (type === 'file') {
      const file = files[0];
      
      if (validateFile(file, structure.fileType, 36700165)) {
        setValues(prev => ({ ...prev, [name]: file }));
        setFilePreview({ url: URL.createObjectURL(file), type: file.type });
      }
    } else {
      setValues(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const handleMultipleTextChange = (index, value) => {
    const newTexts = [...multipleTexts];
    newTexts[index] = value;
    setMultipleTexts(newTexts);
    setValues(prev => ({ ...prev, [structure.field]: newTexts }));
  };

  const addMultipleText = () => {
    if (multipleTexts.length < 10) {
      setMultipleTexts([...multipleTexts, '']);
    } else {
      alert('Maximum of 10 texts only');
    }
  };

  const removeMultipleText = (index) => {
    const newTexts = multipleTexts.filter((_, i) => i !== index);
    setMultipleTexts(newTexts);
    setValues(prev => ({ ...prev, [structure.field]: newTexts }));
  };

  if (
    ['paypalClientId', 'paypalClientSecret'].includes(structure.field) &&
    !showPaypalFields
  ) {
    return null;
  }

  const commonStyles = {
    marginBottom: 2,
    '& .MuiInputBase-root': {
      borderRadius: 1
    }
  };

  switch (structure.inputType) {
    case 'text':
    case 'date':
      return (
        <TextField
          fullWidth
          size="small"
          id={`${id}_${structure.field}`}
          name={structure.field}
          label={structure.headerName}
          type={structure.inputType}
          value={values[structure.field] || ''}
          onChange={handleChange}
          disabled={structure.isDisabled}
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={structure.inputType === 'date' ? { min: currentDate } : {}}
          sx={commonStyles}
        />
      );
    case 'password':
      return (
        <FormControl fullWidth variant="outlined" size="small" sx={commonStyles}>
          <InputLabel htmlFor={`${id}_${structure.field}`}>{structure.headerName}</InputLabel>
          <OutlinedInput
            id={`${id}_${structure.field}`}
            name={structure.field}
            label={structure.headerName}
            type={values[`show${structure.field}`] ? 'text' : 'password'}
            value={values[structure.field] || ''}
            onChange={handleChange}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setValues(prev => ({ ...prev, [`show${structure.field}`]: !values[`show${structure.field}`] }))}
                  edge="end"
                  size="small"
                >
                  {values[`show${structure.field}`] ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>
      );
    case 'switch':
      return (
        <FormControlLabel
          control={
            <Switch
              checked={values[structure.field] || false}
              onChange={handleChange}
              name={structure.field}
              size="small"
            />
          }
          label={structure.headerName}
        />
      );
    case 'select':
    case 'select2':
    case 'select9':
      return (
        <FormControl fullWidth size="small" sx={commonStyles}>
          <InputLabel>{structure.headerName}</InputLabel>
          <Select
            value={values[structure.field] || ''}
            onChange={handleChange}
            name={structure.field}
            label={structure.headerName}
          >
            {selectOptions.map((option, index) => (
              <MenuItem key={index} value={option.Name}>{option.Name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    case 'file':
    case 'videoFile':
    case 'pdfFile':
      return (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <label htmlFor={`${id}_${structure.field}`}>
              <input
                style={{ display: 'none' }}
                id={`${id}_${structure.field}`}
                name={structure.field}
                type="file"
                onChange={handleChange}
              />
              <Fab
                color="secondary"
                size="small"
                component="span"
                aria-label="add"
                variant="extended"
              >
                <AddIcon /> {structure.headerName}
              </Fab>
            </label>
            {filePreview && (
              <Box sx={{ width: 100, height: 100, overflow: 'hidden' }}>
                {filePreview.type.startsWith('image/') && (
                  <img src={filePreview.url} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
                {filePreview.type.startsWith('video/') && (
                  <video src={filePreview.url} controls style={{ width: '100%', height: '100%' }} />
                )}
                {filePreview.type.startsWith('audio/') && (
                  <audio src={filePreview.url} controls style={{ width: '100%' }} />
                )}
                {filePreview.type === 'application/pdf' && (
                  <embed src={filePreview.url} type="application/pdf" width="100%" height="100%" />
                )}
              </Box>
            )}
            {values[structure.field] && <Typography variant="body2">{values[structure.field].name}</Typography>}
          </Stack>
        </Box>
      );
    case 'multiText':
      return (
        <TextareaAutosize
          id={`${id}_${structure.field}`}
          name={structure.field}
          placeholder={structure.headerName}
          minRows={4}
          style={{
            width: '100%',
            padding: '8px',
            marginBottom: '16px',
            borderRadius: '4px',
            borderColor: 'rgba(0, 0, 0, 0.23)',
            fontSize: '14px'
          }}
          value={values[structure.field] || ''}
          onChange={handleChange}
        />
      );
    case 'multipleText':
      return (
        <Box sx={{ mb: 2 }}>
          {multipleTexts.map((text, index) => (
            <Stack key={index} direction="row" spacing={1} sx={{ mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                value={text}
                onChange={(e) => handleMultipleTextChange(index, e.target.value)}
                label={`${structure.headerName} ${index + 1}`}
              />
              <IconButton onClick={() => removeMultipleText(index)} size="small">
                <CancelIcon />
              </IconButton>
            </Stack>
          ))}
          {multipleTexts.length < 10 && (
            <Button
              startIcon={<AddIcon />}
              onClick={addMultipleText}
              variant="outlined"
              size="small"
            >
              Add {structure.headerName}
            </Button>
          )}
        </Box>
      );
    case 'heading':
      return <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>{structure.title}</Typography>;
    default:
      return null;
  }
};

export default FormField;

